import type {CreateExtArgs, Extension, ExtensionAPI} from '@wix/document-manager-core'
import _ from 'lodash'
import type {CompRef, Pointer, PossibleViewModes} from '@wix/document-services-types'
import {getComponentType} from '../../utils/dalUtils'
import {findPrev, findNext, findNearestValidIndex} from '../../utils/arrayUtils'
import {CONTROLLER_TYPE, VIEW_MODES, MASTER_PAGES_SECTIONS, PAGES_CONTAINER_TYPE} from '../../constants/constants'
import {isRefPointer} from '../../utils/refStructureUtils'
import {isRepeatedComponent} from '../../utils/repeaterUtils'
import {deepClone} from '@wix/wix-immutable-proxy'
export interface ComponentsArrangementAPI extends ExtensionAPI {
    moveToIndex(compPointer: CompRef, index: number): void
    getNewIndex(compPointer: Pointer, parentPointer: Pointer, preferredIndex: number): number
    isCompArrangeable(compPointer: CompRef): boolean
    masterPage: {
        shouldUseMasterPageArrangement(parentPointer: Pointer): boolean | void
        findValidIndexForChild(compPointer: Pointer, parentPointer: Pointer, preferredIndex: number): number
    }
}

export interface ComponentsArrangementExtensionAPI extends ExtensionAPI {
    componentsArrangement: ComponentsArrangementAPI
}

const createExtension = (): Extension => {
    const createExtensionAPI = (createExtArgs: CreateExtArgs): ComponentsArrangementExtensionAPI => {
        const isDesktopMasterPage = (compPointer: Pointer) => {
            const {pointers} = createExtArgs
            return pointers.components.isSameComponent(
                compPointer,
                pointers.structure.getMasterPage(VIEW_MODES.DESKTOP)
            )
        }

        const isFixedPosition = (compPointer: Pointer) => {
            const {pointers, dal} = createExtArgs
            const isFixedPointer = pointers.getInnerPointer(compPointer, 'layout.fixedPosition')
            return !!dal.get(isFixedPointer)
        }

        const isSiteShowOnAllPagesZindexMigrated = () => {
            const {pointers, dal} = createExtArgs
            const masterPageData = dal.get(pointers.data.getDataItemFromMaster('masterPage'))
            const layoutSettings = _.get(masterPageData, ['layoutSettings']) || {}
            return _.get(layoutSettings, ['soapCompsAroundPagesContainer'], false)
        }

        const shouldUseMasterPageArrangement = (parentPointer: Pointer) =>
            isDesktopMasterPage(parentPointer) && isSiteShowOnAllPagesZindexMigrated()
        const getPagesContainer = (viewMode: PossibleViewModes) => {
            const {pointers} = createExtArgs
            return pointers.structure.getPagesContainer(viewMode)
        }
        const isMasterPageSectionByCompType = (compType: string, viewMode: PossibleViewModes) =>
            _.includes(MASTER_PAGES_SECTIONS[viewMode], compType)
        const isMasterPageSection = (compPointer: CompRef) => {
            const {dal} = createExtArgs
            return isMasterPageSectionByCompType(getComponentType(dal, compPointer), compPointer.type)
        }

        const getPagesContainerRangeStart = (childrenPointers: Pointer[], pagesContainerIndex: number) => {
            if (pagesContainerIndex === 0) {
                return pagesContainerIndex
            }

            return (
                findPrev(
                    childrenPointers,
                    child => isMasterPageSection(child as CompRef) || isFixedPosition(child),
                    pagesContainerIndex
                ) + 1
            )
        }

        const getPagesContainerRangeEnd = (childrenPointers: Pointer[], pagesContainerIndex: number) => {
            const lastChildIndex = childrenPointers.length - 1

            if (pagesContainerIndex === lastChildIndex) {
                return pagesContainerIndex
            }

            const firstSectionFromRight = findNext(
                childrenPointers,
                child => isMasterPageSection(child as CompRef) || isFixedPosition(child),
                pagesContainerIndex
            )

            return firstSectionFromRight === -1 ? lastChildIndex : firstSectionFromRight - 1
        }

        const findPagesContainerRange = (pagesContainer: Pointer, childrenPointers: Pointer[]) => {
            const pagesContainerIndex = _.findIndex(childrenPointers, {id: pagesContainer.id})

            return [
                getPagesContainerRangeStart(childrenPointers, pagesContainerIndex),
                getPagesContainerRangeEnd(childrenPointers, pagesContainerIndex)
            ]
        }

        const isPagesContainerByType = (compType: string) => compType === PAGES_CONTAINER_TYPE

        const isPagesContainer = (compPointer: Pointer) => {
            const {dal} = createExtArgs
            return isPagesContainerByType(getComponentType(dal, compPointer))
        }
        const getRange = (start: number, end: number): [number, number] => [start, end]
        const rejectEmptyRanges = (ranges: [number, number][]) =>
            _.reject(ranges, ([startIndex, endIndex]) => startIndex > endIndex)
        const getValidIndexRangesForChild = (compPointer: Pointer, parentPointer: Pointer) => {
            const {pointers} = createExtArgs
            const children = pointers.structure.getChildren(parentPointer)
            const childrenWithoutMovingChild = _.reject(children, child =>
                pointers.components.isSameComponent(compPointer, child)
            )
            const pagesContainer = getPagesContainer(compPointer.type as PossibleViewModes)
            const [soapGroupStart, soapGroupEnd] = findPagesContainerRange(pagesContainer, childrenWithoutMovingChild)

            const shouldContainInSoapGroup =
                !(isMasterPageSection(compPointer as CompRef) && !isPagesContainer(compPointer)) &&
                !isFixedPosition(compPointer)

            return shouldContainInSoapGroup
                ? [getRange(soapGroupStart, soapGroupEnd + 1)]
                : rejectEmptyRanges([
                      getRange(0, soapGroupStart),
                      getRange(soapGroupEnd + 1, childrenWithoutMovingChild.length)
                  ])
        }

        const findValidIndexForChild = (compPointer: Pointer, parentPointer: Pointer, preferredIndex: number) => {
            const {pointers} = createExtArgs

            const children = pointers.structure.getChildren(parentPointer)
            const childrenWithoutMovingChild = _.reject(children, child =>
                pointers.components.isSameComponent(compPointer, child)
            )
            preferredIndex = _.isUndefined(preferredIndex) ? childrenWithoutMovingChild.length : preferredIndex

            const validIndexRangesForChild = getValidIndexRangesForChild(compPointer, parentPointer)

            return findNearestValidIndex(childrenWithoutMovingChild, preferredIndex, validIndexRangesForChild)
        }

        const getNewIndex = (compPointer: Pointer, parentPointer: Pointer, preferredIndex: number) =>
            shouldUseMasterPageArrangement(parentPointer)
                ? findValidIndexForChild(compPointer, parentPointer, preferredIndex)
                : preferredIndex
        const isCompArrangeable = (compPointer: CompRef) => {
            const {pointers, dal} = createExtArgs
            if (pointers.structure.isPage(compPointer)) {
                return false
            }
            const componentType = getComponentType(dal, compPointer)

            if (componentType === CONTROLLER_TYPE) {
                return false
            }

            if (isRefPointer(compPointer)) {
                return false
            }

            const parentPointer = pointers.structure.getParent(compPointer)
            const isRepeatedComp = isRepeatedComponent(compPointer.id)
            const isParentRepeatedComponent = parentPointer && isRepeatedComponent(parentPointer.id)

            if (isRepeatedComp && !isParentRepeatedComponent) {
                return false
            }
            return true
        }
        const canMoveCompToIndex = (compPointer: CompRef, index: number) => {
            const {pointers} = createExtArgs
            if (!isCompArrangeable(compPointer)) {
                return false
            }
            const parentPointer = pointers.structure.getParent(compPointer)
            const childrenPointers = pointers.structure.getChildren(parentPointer!)
            return _.isFinite(index) && index >= 0 && index < childrenPointers.length
        }
        const moveToIndex = (compPointer: CompRef, index: number) => {
            const {dal, pointers} = createExtArgs
            if (!canMoveCompToIndex(compPointer, index)) {
                throw new Error("index is out of children's bounds, cannot move to index")
            }

            const parentPointer = pointers.structure.getParent(compPointer)
            const childrenContainerPointer = pointers.structure.getChildrenContainer(parentPointer!)
            const childrenPointers = pointers.structure.getChildren(parentPointer!)
            const compIndex = _.findIndex(childrenPointers, {id: compPointer.id})
            if (compIndex === index) {
                return
            }
            const newIndex = getNewIndex(compPointer, parentPointer!, index)
            let childArr = dal.get(childrenContainerPointer)

            childArr = deepClone(childArr)
            const compId = compPointer.id
            const ind = childArr.indexOf(compId)
            if (ind !== -1) {
                childArr.splice(ind, 1)
            }
            if (_.isNumber(newIndex)) {
                if (index > childArr.size || index < 0) {
                    throw new Error('Index out of bound')
                }
                childArr.splice(newIndex, 0, compId)
            }
            dal.set(childrenContainerPointer, childArr)
            return newIndex
        }
        return {
            componentsArrangement: {
                moveToIndex,
                getNewIndex,
                isCompArrangeable,
                masterPage: {
                    shouldUseMasterPageArrangement,
                    findValidIndexForChild
                }
            }
        }
    }

    return {
        name: 'componentsArrangement',
        createExtensionAPI
    }
}

export {createExtension}
